/** Shared domain vocabulary. Enums are `as const` objects so they can be used
 *  as runtime values (zod `.options`, switch cases) and as types. */

export const ADMIN_ROLES = ['ADMIN', 'FINANCE_ADMIN', 'DISTRIBUTOR'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const ORG_ROLES = ['OWNER', 'FINANCE', 'VIEWER'] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

export const DONOR_KINDS = ['PERSONAL', 'ORGANIZATION'] as const;
export type DonorKind = (typeof DONOR_KINDS)[number];

/** Ledger account owners. SYSTEM accounts are exempt from the no-negative rule. */
export const ACCOUNT_OWNER_KINDS = ['DONOR', 'ORGANIZATION', 'CAMPAIGN', 'CODE', 'SYSTEM'] as const;
export type AccountOwnerKind = (typeof ACCOUNT_OWNER_KINDS)[number];

/**
 * The kinds of campaign.
 *
 * A campaign is a thing a donor can give towards, with a goal, a deadline and
 * a public page. The kinds differ in what the goal means and who may see the
 * page, not in the money: every campaign's progress is computed from the
 * ledger.
 */
export const CAMPAIGN_KINDS = [
  'GENERAL',
  'REGIONAL',
  'SCHOOL_YEAR',
  'SPONSORSHIP_TRACK',
  'COFUND',
] as const;
export type CampaignKind = (typeof CAMPAIGN_KINDS)[number];

/**
 * The lifecycle of a campaign.
 *
 * `DRAFT` is invisible to donors, `OPEN` is the one they can give to, and
 * `CLOSED` and `ARCHIVED` keep the page readable with its final numbers - a
 * campaign that funded a school year stays on the record.
 */
export const CAMPAIGN_STATUSES = ['DRAFT', 'OPEN', 'CLOSED', 'ARCHIVED'] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

/** What a campaign's goal is measured in: money, or whole seats. */
export const CAMPAIGN_GOAL_UNITS = ['CENTS', 'SEATS'] as const;
export type CampaignGoalUnit = (typeof CAMPAIGN_GOAL_UNITS)[number];

/**
 * A campaign as the public may see it.
 *
 * Every number in here is computed from the ledger. `raisedCents` is null when
 * the campaign has no money yet, and `contributors` is 0: an empty campaign is
 * an empty campaign, and a zero that reads as "we never funded anything" is
 * not the same fact.
 */
export interface PublicCampaign {
  slug: string;
  kind: CampaignKind;
  status: CampaignStatus;
  titleAr: string;
  titleEn: string;
  descriptionAr: string;
  descriptionEn: string;
  goalUnit: CampaignGoalUnit;
  goalAmount: number;
  /** The seat package this campaign funds, when it funds one. */
  packageId: string | null;
  /** When the campaign stops accepting gifts. Null means "no deadline". */
  deadline: string | null;
  /** Money that reached the books for this campaign, net of refunds. */
  raisedCents: number | null;
  /** Whole seats the money funds. */
  seatsFunded: number;
  /** How many donors gave. */
  contributors: number;
}

/**
 * The identity we attach to every authenticated request. `sub` is the
 * CoreIAM user id and is the shared key across systems.
 */
export interface Identity {
  sub: string;
  email?: string;
  tenantId?: string;
  roles: string[];
}

/** A request that passed authentication, plus resolved database roles. */
export interface AuthenticatedActor {
  identity: Identity;
  donorId: string;
  adminRoles: AdminRole[];
}

/** Error envelope returned by every API route. */
export interface ApiError {
  error: {
    code: string;
    message: string;
    /**
     * A closed-set code from the server, beside the envelope code.
     *
     * The envelope code says *which* failure; the reason says what a caller
     * can do about it, in words that never change. It is optional because
     * most failures have nothing to add, and it is never prose: the client
     * switches on it, so it has to be a fixed set of strings.
     */
    reason?: string;
    details?: unknown;
  };
}
